class PromotionPopup extends HTMLElement {
  constructor() {
    super();
    this.popup = this.closest('.modal--popup');
    this.dismissTodayCookie = '_halo_promotion_popup_dismiss_today';
    this.convertedCookie = '_halo_promotion_popup_converted';
    this.expiresDate = Number(this.getAttribute('data-expire')) || 14;
    this.desktopDelay = Number(this.popup.dataset.delayDesktop) || 30000;
    this.mobileDelay = Number(this.popup.dataset.delayMobile) || 40000;
    this.desktopScrollDepth = Number(this.popup.dataset.scrollDesktop) || 35;
    this.mobileScrollDepth = Number(this.popup.dataset.scrollMobile) || 40;
    this.enableExitIntent = this.popup.dataset.exitIntent === 'true';
    this.pauseFloatingTools = this.popup.dataset.pauseFloatingTools === 'true';
    this._storage = this.getBestStorage();
    this.overlay = this.popup.querySelector('[id^="Drawer-Overlay-"]');
    this.content = this.popup.querySelector('.popup__inner');
    this.isOpen = false;
    this.hasTriggered = false;
    this.connectedAt = Date.now();
    this.triggerTimer = null;

    this.boundScroll = this.handleScroll.bind(this);
    this.boundExitIntent = this.handleExitIntent.bind(this);
    this.boundKeydown = this.handleKeydown.bind(this);


    this.querySelector('.popup__close')?.addEventListener('click', this.dismissForConfiguredPeriod.bind(this));
    this.querySelector('[data-promotion-decline]')?.addEventListener('click', this.dismissForConfiguredPeriod.bind(this));
    this.overlay?.addEventListener('click', this.dismissForConfiguredPeriod.bind(this));

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (e.target.matches('.section-promotion-popup')) this.setOpenPopup(0);
      });

      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.target.matches('.section-promotion-popup')) this.setClosePopup();
      });
    }
  }

  connectedCallback() {
    if (this.popup.dataset.hideMobileTools === 'true') {
      document.body.classList.add('fv-hide-mobile-promo-tools');
    }

    try {
      this.load();
    } catch (_) {
      if (!Shopify.designMode) this.scheduleEngagementTriggers();
    }
  }

  load() {
    this.observeEmailSuccess();

    if (this.popup.dataset.hideOnCart === 'true' && this.popup.dataset.pageType === 'cart') return;

    const hasSuccess = this.querySelector('.email-signup__message--success');
    const hasMessage = this.querySelector('.email-signup__message');

    if (hasSuccess) {
      this.setStoreDays(this.convertedCookie, 'converted', 365);
      if (hasMessage) this.popup.classList.add('active');
      if (!Shopify.designMode) this.setOpenPopup(0);
      return;
    }

    if (this.getStore(this.dismissTodayCookie) || this.getStore(this.convertedCookie)) return;

    if (hasMessage) this.popup.classList.add('active');
    if (!Shopify.designMode) this.scheduleEngagementTriggers();
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  scheduleEngagementTriggers() {
    if (this.hasTriggered || this.isOpen) return;

    const delay = this.isMobileViewport() ? this.mobileDelay : this.desktopDelay;
    this.triggerTimer = window.setTimeout(() => this.triggerPopup('delay'), delay);
    window.addEventListener('scroll', this.boundScroll, { passive: true });

    if (this.enableExitIntent && !this.isMobileViewport()) {
      document.addEventListener('mouseout', this.boundExitIntent);
    }
  }

  handleScroll() {
    const scrollableHeight = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
    const scrollProgress = (window.scrollY / scrollableHeight) * 100;
    const threshold = this.isMobileViewport() ? this.mobileScrollDepth : this.desktopScrollDepth;

    if (scrollProgress >= threshold) this.triggerPopup('scroll');
  }

  handleExitIntent(event) {
    if (Date.now() - this.connectedAt < 5000) return;
    if (!event.relatedTarget && event.clientY <= 10) this.triggerPopup('exit');
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) this.dismissForConfiguredPeriod();
  }

  triggerPopup(reason) {
    if (this.hasTriggered || this.isOpen) return;

    this.hasTriggered = true;
    this.popup.dataset.triggerReason = reason;
    this.teardownTriggers();
    this.setOpenPopup(0);
  }

  teardownTriggers() {
    if (this.triggerTimer) window.clearTimeout(this.triggerTimer);
    this.triggerTimer = null;
    window.removeEventListener('scroll', this.boundScroll);
    document.removeEventListener('mouseout', this.boundExitIntent);
  }

  disconnectedCallback() {
    this.teardownTriggers();
    document.removeEventListener('keydown', this.boundKeydown);
    this.successObserver?.disconnect();
  }

  observeEmailSuccess() {
    const target = this.querySelector('form') || this;
    this.successObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.classList.contains('email-signup__message--success')) {
            this.setStoreDays(this.convertedCookie, 'converted', 365);
            this.popup.classList.add('active');
            this.setOpenPopup(0);
          }
        });
      });
    });
    this.successObserver.observe(target, { childList: true, subtree: true });
  }

  setOpenPopup(timeToShow = 0) {
    window.setTimeout(() => {
      if (this.isOpen) return;

      this.isOpen = true;
      this.activeElement = document.activeElement;
      this.teardownTriggers();
      this.popup.setAttribute('open', 'true');
      this.animateOpen();
      document.body.classList.add('overflow-hidden');
      if (this.pauseFloatingTools) document.body.classList.add('fv-promotion-popup-open');
      document.documentElement.setAttribute('scroll-lock', '');
      document.addEventListener('keydown', this.boundKeydown);

      window.setTimeout(() => {
        if (!this.isOpen) return;
        const focusElement = this.popup.querySelector('.field__input, button, [href]');
        trapFocus(this.content, focusElement);
      }, 320);
    }, timeToShow);
  }

  setClosePopup() {
    this.closePopup();
  }

  dismissForConfiguredPeriod() {
    this.setStoreDays(this.dismissTodayCookie, 'dismissed', this.expiresDate);
    this.closePopup();
  }

  closePopup() {
    if (!this.isOpen && !this.popup.hasAttribute('open')) return;

    this.isOpen = false;
    this.animateClose();
    document.removeEventListener('keydown', this.boundKeydown);

    window.setTimeout(() => {
      this.popup.removeAttribute('open');
      document.body.classList.remove('overflow-hidden', 'fv-promotion-popup-open');
      document.documentElement.removeAttribute('scroll-lock');
      removeTrapFocus(this.activeElement);
    }, 220);
  }

  animateOpen() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const from = this.isMobileViewport() ? 'translate(-50%, 100%)' : 'translate(-50%, -40%)';
    const to = this.isMobileViewport() ? 'translate(-50%, 0)' : 'translate(-50%, -50%)';
    Motion.timeline([
      [this.content, { opacity: [0, 1], transform: [from, to] }, { duration: 0.3, at: '-0.15' }]
    ]).finished;
  }

  animateClose() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const from = this.isMobileViewport() ? 'translate(-50%, 0)' : 'translate(-50%, -50%)';
    const to = this.isMobileViewport() ? 'translate(-50%, 100%)' : 'translate(-50%, -40%)';
    Motion.timeline([
      [this.content, { opacity: [1, 0], transform: [from, to] }, { duration: 0.2 }]
    ]).finished;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  setStoreDays(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + 24 * 60 * 60 * 1000 * days);
    this.setStoreUntil(name, value, d);
  }

  setStoreUntil(name, value, date) {
    try {
      if (!this._storage) return;
      var payload = { v: value, exp: date.getTime() };
      this._storage.setItem(name, JSON.stringify(payload));
    } catch (_) {}
  }

  getStore(name) {
    try {
      if (!this._storage) return null;
      var raw = this._storage.getItem(name);
      if (!raw) return null;
      var payload = JSON.parse(raw);
      if (!payload || typeof payload.exp !== 'number') return null;
      if (Date.now() > payload.exp) {
        this._storage.removeItem(name);
        return null;
      }
      return payload.v;
    } catch (_) { return null; }
  }

  getBestStorage() {
    try {
      var k = '__pp_test__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      return window.localStorage;
    } catch (_) {}
    try {
      var k2 = '__pp_test__';
      window.sessionStorage.setItem(k2, '1');
      window.sessionStorage.removeItem(k2);
      return window.sessionStorage;
    } catch (_) {}
    return null;
  }
}
if (!customElements.get('promotion-popup')) customElements.define('promotion-popup', PromotionPopup);